import {
  Component,
  OnDestroy,
  computed,
  inject,
  input,
  output,
  signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { startOfDay } from 'date-fns';
import { filter, map } from 'rxjs';

import { EntityLabelsTranslationService } from '@gfe/core/services/entity-labels-translation/entity-labels-translation.service';
import { TooltipComponent } from '@gfe/shared/components/tooltip/tooltip.component';

import { ICONS } from '@core/services/icons/icons.service';
import { isNil } from '@core/utils/shared-utils';

import {
  CalendarEvent,
  CalendarEventChange
} from '@features/components/visualization/calendar/models/calendar.models';
import {
  eventColor,
  eventInk,
  formatTimeRange,
  tintFill
} from '@features/components/visualization/calendar/utils/calendar-layout.utils';

import { VisuItemLabel } from '../../common/models/label.model';
import { CalendarEventCutoffComponent } from '../calendar-event-cutoff/calendar-event-cutoff.component';
import {
  TimeRange,
  computeMovedTimes,
  computeMovedTimesAcrossDays,
  computeResizedTimes,
  computeResizedTimesToTarget,
  pxToMinutes,
  snapMinutes
} from '../utils/calendar-drag.utils';

/** Drag interaction mode. */
type DragMode = 'move' | 'resize-start' | 'resize-end';

/** Minimum rendered height (px) while resizing so the event never collapses. */
const MIN_PX = 15;

/** Movement threshold (px) below which a pointer interaction is treated as a click, not a drag. */
const DRAG_THRESHOLD_PX = 3;

/** Used when the entity layer produced no colour for the event. */
const FALLBACK_COLOR = '#4e4c4c';

/** Hairline on the three sides that are not the type rail. */
const EDGE_BORDER = '1px solid var(--border-weak)';

/** Ring drawn round the block a drag is acting on. */
const DRAG_RING = '0 0 0 2px var(--accent-focus)';

/** Opacity of the blocks a drag is not acting on, so the target stands out. */
const DIMMED_OPACITY = 0.42;

/** Stacking layer of an idle block; the now-line sits above it. */
const BLOCK_Z_INDEX = 3;

/** Corner radius of a closed block edge, matching the design system's small radius. */
const BLOCK_RADIUS_PX = 2;

/** Thickness of the type rail, i.e. the block's top border when it is not a continuation. */
const RAIL_PX = 3;

/**
 * Gutter kept clear at the right of every block, so two concurrent events
 * tiled side by side read as two blocks rather than one divided band, and so a
 * block never touches the next day's column.
 */
const COLUMN_GUTTER_PX = 3;

@Component({
  selector: 'app-calendar-event',
  imports: [TooltipComponent, CalendarEventCutoffComponent],
  templateUrl: './calendar-event.component.html'
})
export class CalendarEventComponent implements OnDestroy {
  private readonly entityLabelsTranslation = inject(EntityLabelsTranslationService);
  private readonly translate = inject(TranslateService);

  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  event = input.required<CalendarEvent>();
  /** Vertical scale of the calendar grid: pixels per minute. */
  pixelsPerMinute = input<number>(0);
  /** When false, behaves exactly as before: no handles, no drag (click still opens the tooltip). */
  draggable = input<boolean>(false);
  /** Column dates left→right for the current view (length 1 in day view). */
  days = input<Date[]>([]);
  /**
   * Whether a drag is under way anywhere in the calendar. Set by the parent from
   * `draggingChange`, so a block can tell "someone else is being dragged" from
   * "nothing is happening" and dim itself accordingly.
   */
  dragActive = input<boolean>(false);
  /** Emitted (front-only) when the event has been moved or resized by the user. */
  eventChange = output<CalendarEventChange>();
  /** Raised as this block starts and stops being dragged. */
  draggingChange = output<boolean>();

  protected readonly ICONS = ICONS;

  private eventClicked = signal<boolean>(false);

  // --- Drag/resize state ---------------------------------------------------
  /** Whether a pointer interaction is currently in progress. */
  protected readonly dragging = signal<boolean>(false);
  /** Live preview top (px) while dragging; null when not dragging. */
  protected readonly previewTop = signal<number | null>(null);
  /** Live preview height (px) while dragging; null when not dragging. */
  protected readonly previewHeight = signal<number | null>(null);
  /** Live horizontal translate (px) while moving across columns; null when idle. */
  protected readonly previewTranslateX = signal<number | null>(null);
  /** CSS transform bound by the template during a move. */
  protected readonly previewTransform = computed(() => {
    const tx = this.previewTranslateX();
    return tx === null ? 'none' : `translateX(${tx}px)`;
  });

  /**
   * Everything the drag is not acting on recedes, so the block being moved or
   * resized reads as the only live thing on a crowded column.
   */
  protected readonly dimmed = computed(() => this.dragActive() && !this.dragging());

  protected readonly dragRing = DRAG_RING;
  protected readonly dimmedOpacity = DIMMED_OPACITY;

  private readonly dragMode = signal<DragMode | null>(null);
  private startY = 0;
  private startX = 0;
  private startColumnIndex = 0;
  private startColumnLeft = 0;
  private gridEl: HTMLElement | null = null;
  private basePositionTop = 0;
  private basePositionHeight = 0;
  private thresholdExceeded = false;
  /** Set right after a real drag so the synthetic click does not toggle the tooltip. */
  private justDragged = false;
  private cleanup: (() => void) | null = null;

  /**
   * Human description of the recurrence rule, or null for a one-off event.
   * Doubles as the condition for the loop glyph on the block: an event has a
   * rule to show exactly when it is recurring.
   */
  protected readonly recurrenceLabel = computed(() => {
    this.currentLang(); // Trigger reactivity

    const event = this.event();
    if (!event.recurrence) return null;

    const { type, customDays, interval = 1 } = event.recurrence;

    switch (type) {
      case 'Daily':
        return interval === 1
          ? this.translate.instant('common.calendar.recurrence.daily')
          : this.translate.instant('common.calendar.recurrence.every_n_days', {
              count: interval
            });
      case 'Weekdays':
        return this.translate.instant('common.calendar.recurrence.weekdays');
      case 'Weekends':
        return this.translate.instant('common.calendar.recurrence.weekends');
      case 'CustomDays': {
        if (!customDays?.length) {
          return this.translate.instant('common.calendar.recurrence.custom_days');
        }
        const names: string[] = this.translate
          .instant('common.calendar.week_days')
          .split(',');
        return customDays.map((day) => names[day]).join(', ');
      }
      default:
        return null;
    }
  });

  /** Top position (px) bound by the template: preview while dragging, otherwise the event position. */
  protected readonly renderedTop = computed(
    () => this.previewTop() ?? this.event().position?.top ?? 0
  );
  /** Height (px) bound by the template: preview while dragging, otherwise the event position. */
  protected readonly renderedHeight = computed(
    () => this.previewHeight() ?? this.event().position?.height ?? 0
  );

  /**
   * Everything the cutoff marker needs, or null when there is nothing to draw.
   * Both the offset and the deadline must be present: `cutoffOffsetPx` already
   * returns null for an event without a cutoff, so this only re-states that
   * pairing in a shape the template can guard on once.
   */
  protected readonly cutoffMarker = computed(() => {
    const event = this.event();
    const offsetPx = event.position?.cutoffOffset;
    if (offsetPx == null || event.cutoff == null) return null;

    return {
      offsetPx: offsetPx + this.cutoffTopShiftPx(),
      time: this.previewCutoff() ?? event.cutoff,
      color: eventColor(event) ?? FALLBACK_COLOR
    };
  });

  /**
   * What `cutoffOffset` has to be corrected by before the marker can use it as
   * a `top`, summing two shifts that both move the origin the offset is
   * measured from:
   *
   * - the marker is absolutely positioned inside the block, so its `top`
   *   resolves against the padding box, which starts below the type rail, while
   *   `cutoffOffset` is measured from the block's outer edge;
   * - resizing the start edge moves the block's top but not the deadline, so
   *   the distance between the two changes for the length of the gesture.
   */
  private readonly cutoffTopShiftPx = computed(() => {
    const event = this.event();
    const railPx = event.position?.continuesBefore ? 0 : RAIL_PX;

    const previewTop = this.previewTop();
    const resizeShift =
      this.dragMode() === 'resize-start' && previewTop !== null
        ? (event.position?.top ?? 0) - previewTop
        : 0;

    return resizeShift - railPx;
  });

  /**
   * The cutoff's landing time while the whole block is being moved, or null
   * when there is nothing to preview. A move carries the deadline along with
   * the window; a resize leaves it where it is, so only `move` shifts it.
   */
  private readonly previewCutoff = computed(() => {
    const preview = this.previewTimes();
    const cutoff = this.event().cutoff;
    if (!preview || cutoff == null || this.dragMode() !== 'move') return null;

    return new Date(
      cutoff.getTime() + (preview.start.getTime() - this.event().start.getTime())
    );
  });

  /** The schedule's own hue, worn by the type rail. */
  private readonly railColor = computed(() => eventColor(this.event()) ?? FALLBACK_COLOR);

  /** Wash filling the block: the entity layer's background hue, barely there. */
  protected readonly blockFill = computed(() =>
    tintFill(this.event().backgroundColor ?? FALLBACK_COLOR)
  );

  /** Ink for the mono meta line, legible on the block's own tint in both themes. */
  protected readonly metaInk = computed(() => eventInk(this.railColor()));

  /**
   * The type rail. Dropped when the block is the tail of a window that started
   * earlier: an open top edge is what makes it read as a continuation.
   */
  protected readonly railBorder = computed(() =>
    this.event().position?.continuesBefore
      ? 'none'
      : `${RAIL_PX}px solid ${this.railColor()}`
  );

  protected readonly edgeBorder = EDGE_BORDER;

  protected readonly bottomBorder = computed(() =>
    this.event().position?.continuesAfter ? 'none' : EDGE_BORDER
  );

  /** Corners are squared off on whichever edge is a cut at midnight. */
  protected readonly blockRadius = computed(() => {
    const position = this.event().position;
    const top = position?.continuesBefore ? 0 : BLOCK_RADIUS_PX;
    const bottom = position?.continuesAfter ? 0 : BLOCK_RADIUS_PX;
    return `${top}px ${top}px ${bottom}px ${bottom}px`;
  });

  protected readonly blockWidth = computed(
    () => `calc(${this.event().position?.width ?? 100}% - ${COLUMN_GUTTER_PX}px)`
  );

  /**
   * A fixed layer, so every block forms its own stacking context: without one,
   * a block's cutoff zone and content would compete with a neighbour's on the
   * column's single z-axis. Below the now-line, which sits above the events.
   */
  protected readonly baseZIndex = BLOCK_Z_INDEX;

  /**
   * The times the drag would commit, or null when no drag is in flight.
   *
   * Produced by `resolveDragTimes`, the same method `onPointerUp` commits from,
   * so what the block reads out and what gets saved cannot drift apart. It is a
   * signal set from the pointer handler rather than a computed off the preview
   * geometry, because the geometry alone is not enough: a block cut at midnight
   * has `top = 0` while its event starts the day before, and a cross-day move
   * needs the column under the cursor.
   */
  private readonly previewTimes = signal<TimeRange | null>(null);

  /**
   * The block's time range: where the drag would land, or its stored times when
   * idle.
   */
  protected readonly timeRangeLabel = computed(() => {
    const preview = this.previewTimes();
    const event = this.event();

    return preview
      ? formatTimeRange(preview.start, preview.end)
      : formatTimeRange(event.start, event.end);
  });

  /**
   * A block too short for its own meta row borrows the title line while being
   * dragged, so there is feedback at every event length.
   */
  protected readonly showTimesInsteadOfTitle = computed(
    () => this.previewTimes() !== null && this.renderedHeight() < 32
  );

  protected onEventClick(): void {
    // A real drag ends with a synthetic click we must ignore so it does not toggle the tooltip.
    if (this.justDragged) {
      this.justDragged = false;
      return;
    }
    this.eventClicked.set(true);
  }

  protected onClickOutside() {
    this.eventClicked.set(false);
  }

  /** Start a drag/resize interaction. Only active when the event is draggable. */
  protected onPointerDown(event: PointerEvent, mode: DragMode): void {
    if (!this.draggable()) return;

    // Resize handles must not bubble up to the body's move handler.
    if (mode !== 'move') {
      event.stopPropagation();
    }

    this.dragMode.set(mode);
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.basePositionTop = this.event().position?.top ?? 0;
    this.basePositionHeight = this.event().position?.height ?? 0;
    this.thresholdExceeded = false;
    this.gridEl = (event.target as HTMLElement).closest(
      '[data-calendar-grid]'
    ) as HTMLElement | null;
    const startCol = this.resolveColumn(event.clientX);
    this.startColumnIndex = startCol?.index ?? 0;
    this.startColumnLeft = startCol?.rect.left ?? 0;

    const onMove = (e: PointerEvent) => this.onPointerMove(e);
    const onUp = (e: PointerEvent) => this.onPointerUp(e);
    // A touch, a browser gesture or a context menu ends the sequence with
    // `pointercancel` and no `pointerup`, which would otherwise leave the parent
    // believing a drag is still in flight.
    const onCancel = () => this.resetDrag();
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onCancel);
    this.cleanup = () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onCancel);
    };
  }

  private onPointerMove(event: PointerEvent): void {
    const mode = this.dragMode();
    if (!mode) return;

    const deltaY = event.clientY - this.startY;
    const deltaX = event.clientX - this.startX;

    if (!this.thresholdExceeded) {
      if (Math.abs(deltaY) < DRAG_THRESHOLD_PX && Math.abs(deltaX) < DRAG_THRESHOLD_PX) {
        return;
      }
      this.thresholdExceeded = true;
      this.dragging.set(true);
      this.draggingChange.emit(true);
    }

    // Snap the preview to the same 15-min grid the commit uses, so the block shows
    // its real landing slot. A resize is additionally clamped to the day's bounds so
    // it never grows outside the calendar when the cursor leaves the grid; a move is
    // not — see `rollMovePreview`.
    const snappedDeltaY = this.snapDeltaPx(deltaY);
    const dayHeight = this.dayHeightPx();
    const baseTop = this.basePositionTop;
    const baseHeight = this.basePositionHeight;

    switch (mode) {
      case 'move': {
        const { top, dayRoll } = this.rollMovePreview(baseTop + snappedDeltaY);
        this.previewTop.set(top);
        this.previewHeight.set(baseHeight);
        this.previewTranslateX.set(this.snapTranslateX(event.clientX, deltaX, dayRoll));
        break;
      }
      case 'resize-start': {
        const maxTop = baseTop + baseHeight - MIN_PX;
        const newTop = Math.max(0, Math.min(baseTop + snappedDeltaY, maxTop));
        this.previewTop.set(newTop);
        this.previewHeight.set(baseTop + baseHeight - newTop);
        break;
      }
      case 'resize-end': {
        const maxHeight = Math.max(MIN_PX, dayHeight - baseTop);
        this.previewTop.set(baseTop);
        this.previewHeight.set(
          Math.max(MIN_PX, Math.min(baseHeight + snappedDeltaY, maxHeight))
        );
        break;
      }
    }

    this.previewTimes.set(this.resolveDragTimes(event.clientX, event.clientY, mode));
  }

  /**
   * The times this pointer position would commit. Single source for both the
   * live readout on the block and the change emitted on drop, so the text a
   * user reads while dragging is exactly what lands.
   */
  private resolveDragTimes(clientX: number, clientY: number, mode: DragMode): TimeRange {
    const calEvent = this.event();
    const crossDay = this.days().length > 1;
    const deltaMinutes = pxToMinutes(clientY - this.startY, this.pixelsPerMinute());

    if (mode === 'move') {
      if (!crossDay) {
        return computeMovedTimes(calEvent.start, calEvent.end, deltaMinutes);
      }
      const endIndex = this.resolveColumn(clientX)?.index ?? this.startColumnIndex;
      return computeMovedTimesAcrossDays(
        calEvent.start,
        calEvent.end,
        endIndex - this.startColumnIndex,
        deltaMinutes
      );
    }

    const edge = mode === 'resize-start' ? 'start' : 'end';
    if (!crossDay) {
      return computeResizedTimes(calEvent.start, calEvent.end, edge, deltaMinutes);
    }

    const col = this.resolveColumn(clientX);
    const index = col?.index ?? this.startColumnIndex;
    const ppm = this.pixelsPerMinute();
    const minutesFromTop = col && ppm > 0 ? (clientY - col.rect.top) / ppm : 0;

    return computeResizedTimesToTarget(
      calEvent.start,
      calEvent.end,
      edge,
      this.columnDate(index),
      Math.max(0, Math.min(1440, minutesFromTop))
    );
  }

  /** Snap a pixel delta to the nearest 15-min grid step, expressed back in pixels. */
  private snapDeltaPx(deltaPx: number): number {
    const ppm = this.pixelsPerMinute();
    if (ppm <= 0) return deltaPx;
    return snapMinutes(pxToMinutes(deltaPx, ppm)) * ppm;
  }

  /** Full-day height in pixels, or Infinity when the scale is unknown (no clamp). */
  private dayHeightPx(): number {
    const ppm = this.pixelsPerMinute();
    return ppm > 0 ? ppm * 1440 : Infinity;
  }

  /**
   * Where a move preview sits vertically, given the block top the raw drag asks
   * for: its top within a single column, and how many whole days past midnight
   * the drag has rolled the window.
   *
   * A move is deliberately not clamped to the column — `computeMovedTimesAcrossDays`
   * commits a window that may land on the next or previous day, which is how a
   * block is dragged across midnight without leaving its column. Pinning the
   * preview at the column's foot instead would draw the block at 23:00 while it
   * reads out, and saves, 01:30 the day after. Folding the overflow into whole
   * columns keeps the geometry on the same instant as the commit: both are
   * `(segment top + snapped delta)` split into days and a remainder.
   */
  private rollMovePreview(absoluteTop: number): { top: number; dayRoll: number } {
    const dayHeight = this.dayHeightPx();
    if (!Number.isFinite(dayHeight) || dayHeight <= 0) {
      return { top: absoluteTop, dayRoll: 0 };
    }

    const dayRoll = Math.floor(absoluteTop / dayHeight);
    return { top: absoluteTop - dayRoll * dayHeight, dayRoll };
  }

  /**
   * Horizontal move preview snapped to the grid columns: the block jumps by whole
   * columns to sit over the day under the cursor, plus the `dayRoll` days a
   * vertical drag past midnight adds. Falls back to the cursor's own column when
   * the roll lands outside the visible week, and to the raw delta when no grid is
   * available (e.g. day view without a measured grid).
   */
  private snapTranslateX(clientX: number, deltaX: number, dayRoll: number): number {
    const col = this.resolveColumn(clientX);
    if (!col) return deltaX;

    const rolled = this.columnRect(col.index + dayRoll) ?? col.rect;
    return rolled.left - this.startColumnLeft;
  }

  /** Bounding rect of the day column at `index`, or null when it is off the grid. */
  private columnRect(index: number): DOMRect | null {
    const grid = this.gridEl;
    if (!grid || index < 0) return null;

    const col = grid.querySelectorAll<HTMLElement>('[data-calendar-column]')[index];
    return col ? col.getBoundingClientRect() : null;
  }

  private onPointerUp(event: PointerEvent): void {
    const mode = this.dragMode();

    if (mode && this.thresholdExceeded) {
      const times = this.resolveDragTimes(event.clientX, event.clientY, mode);

      this.eventChange.emit({
        uid: this.event().uid,
        start: times.start,
        end: times.end
      });

      // Suppress the synthetic click following a real drag.
      this.justDragged = true;
    }

    this.resetDrag();
  }

  /** Reset all transient drag state and tear down document listeners. */
  private resetDrag(): void {
    // Only announce the end of a drag that was announced as started: a pointer
    // press that never cleared the threshold was a click, not a drag.
    if (this.dragging()) {
      this.draggingChange.emit(false);
    }

    this.dragMode.set(null);
    this.thresholdExceeded = false;
    this.dragging.set(false);
    this.previewTimes.set(null);
    this.previewTop.set(null);
    this.previewHeight.set(null);
    this.previewTranslateX.set(null);
    this.gridEl = null;
    this.startColumnIndex = 0;
    this.startColumnLeft = 0;
    this.startX = 0;
    this.cleanup?.();
    this.cleanup = null;
  }

  /**
   * Resolve the day column nearest to `clientX` from the captured grid.
   *
   * Columns are found by their `data-calendar-column` marker rather than by
   * position among the grid's children: the grid also holds the sticky headers
   * and the hour gutter, so index-among-children would not be the day index.
   */
  private resolveColumn(clientX: number): { index: number; rect: DOMRect } | null {
    const grid = this.gridEl;
    if (!grid) return null;
    const cols = Array.from(grid.querySelectorAll<HTMLElement>('[data-calendar-column]'));
    if (cols.length === 0) return null;

    let best = 0;
    let bestDist = Infinity;
    let bestRect: DOMRect | null = null;
    cols.forEach((col, i) => {
      const r = col.getBoundingClientRect();
      const center = (r.left + r.right) / 2;
      const dist = Math.abs(clientX - center);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
        bestRect = r;
      }
    });
    return { index: best, rect: bestRect ?? cols[best].getBoundingClientRect() };
  }

  /** startOfDay of the column at `index`, falling back to the event's own day. */
  private columnDate(index: number): Date {
    const ds = this.days();
    if (ds.length === 0) return startOfDay(this.event().start);
    const clamped = Math.max(0, Math.min(index, ds.length - 1));
    return startOfDay(ds[clamped]);
  }

  /**
   * A block can be destroyed mid-drag: the events come from the container, so a
   * refresh that changes this occurrence's start changes its track key. Going
   * through `resetDrag` announces the end of the drag, without which the parent
   * would dim the whole calendar forever. Angular runs `ngOnDestroy` before the
   * `DestroyRef` hooks that close the output, so the emit still lands.
   */
  ngOnDestroy(): void {
    this.resetDrag();
  }

  protected readonly tooltipTitle = computed(() => {
    const event = this.event();
    return event.title;
  });

  protected getTooltipLabels(): VisuItemLabel[] {
    const event = this.event();
    const labels = this.eventClicked()
      ? event.labels
      : event.labels.filter((label) => label.level === 0);

    // Translate the labels before returning
    return this.entityLabelsTranslation.translateMarkerLabels(labels);
  }

  /** `13 h 30` / `45 min`. The units read the same in en, fr and es. */
  private formatDuration(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const rest = Math.round(minutes % 60);

    if (hours === 0) return `${rest} min`;
    return rest === 0 ? `${hours} h` : `${hours} h ${String(rest).padStart(2, '0')}`;
  }

  /** Times, duration, cutoff and recurrence — the block's own text, spelled out. */
  private tooltipSummary(): string[] {
    const event = this.event();
    const durationMinutes = (event.end.getTime() - event.start.getTime()) / 60_000;
    const lines = [`${this.timeRangeLabel()} · ${this.formatDuration(durationMinutes)}`];

    const recurrence = this.recurrenceLabel();
    if (recurrence) {
      lines.push(recurrence);
    }

    return lines;
  }

  protected readonly tooltipContent = computed(() => {
    this.currentLang(); // Trigger reactivity

    const labels = this.getTooltipLabels();
    const summary = this.tooltipSummary().join('<br>');

    if (labels.length === 0) {
      return summary;
    }

    let content = summary + '<br><br>';
    const groupedLabels = labels.reduce(
      (acc, label) => {
        if (!acc[label.level]) acc[label.level] = [];
        acc[label.level].push(label);
        return acc;
      },
      {} as Record<number, VisuItemLabel[]>
    );

    Object.keys(groupedLabels)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .forEach((level) => {
        if (parseInt(level) > 0) content += '<br>';
        groupedLabels[parseInt(level)].forEach((label) => {
          content += `<strong>${label.label}:</strong> ${label.value}<br>`;
        });
      });

    return content.trim();
  });
}
